import { observer } from 'mobx-react-lite'
import React, { type FC, useMemo } from 'react'
import classNames from 'classnames'
import { Button, SpinnerLoader, Typography } from 'shared/ui'
import { makeTextFieldAutoFocus, makeTextFieldClear, TextField } from 'shared/ui/TextField'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { Contact } from 'entities/Contacts/model/Contact'
import { useBlockContactModal } from 'entities/Contacts/ui/BlockContactModal'
import { useCallPopUpDefaultContext } from 'widgets/CallPopUp/context/callPopUpDefaultContext'
import { type CallPopUpCallInsightCardsCallTypeStore } from 'widgets/CallPopUp/store/callPopUpCallInsightCardsCallTypeStore'
import styles from './styles.module.scss'

type ICallInsightCardsCallTypeProps = {
  store: CallPopUpCallInsightCardsCallTypeStore
}

export const CallInsightCardsCallType: FC<ICallInsightCardsCallTypeProps> = observer(
  ({ store }) => {
    const defaultStore = useCallPopUpDefaultContext()
    const { contactsStore } = useEntitiesContext()
    const { contactTo } = defaultStore

    const {
      loading,
      search,
      setSearch,
      filteredCallTypes,
      onSelectedCallType,
      handleSkipCallType,
      closeCallType,
      showModal,
      hasCallTypes,
    } = store

    const showFullContent = showModal && (hasCallTypes || loading)
    const fullContact = contactTo ? contactsStore.getItem(contactTo.id) : null
    const contact = fullContact instanceof Contact ? fullContact : null
    const { handleBlock } = useBlockContactModal(contact, {})

    const handleSelectedCallType = (type: string): void => {
      onSelectedCallType(type)
    }

    const content = useMemo(() => {
      if (loading) {
        return (
          <div className={classNames(styles.loading)}>
            <SpinnerLoader ariaLabel={'CallInsightCardsCallType'} size={16} />
          </div>
        )
      }

      if (filteredCallTypes?.length || search) {
        return (
          <div className={styles.list}>
            {!filteredCallTypes?.length && (
              <div className={styles.wrapEmpty}>
                <Typography
                  variant={'body-sm-regular'}
                  color={'var(--content-primary-tertiary)'}
                  ariaLabel={'No results found'}
                >
                  No results found
                </Typography>
              </div>
            )}
            {filteredCallTypes?.map((type) => (
              <Button
                key={type}
                className={styles.callTypeButton}
                onClick={() => handleSelectedCallType(type)}
                typeBtn={'menuItem'}
                aria-label={type}
                titleContent={
                  <div className={styles.callTypeTitle}>
                    <Typography
                      variant={'body-md-regular'}
                      ellipsis
                      tag={'div'}
                      className={styles.leftContent}
                      ariaLabel={'label'}
                      tooltipProps={{
                        label: type,
                        placement: 'top',
                      }}
                    >
                      {type}
                    </Typography>
                  </div>
                }
              />
            ))}
          </div>
        )
      }
    }, [loading, filteredCallTypes, search])

    if (!showFullContent) {
      if (!defaultStore.shouldShowIncomingBlockInsightCard) return null

      return (
        <div className={classNames(styles.section)}>
          <div className={classNames(styles.wrap)}>
            <div className={styles.action}>
              {contact && (
                <Button
                  textClassname={styles.blockButtonText}
                  typeBtn={'menuItem'}
                  text={'Block and report spam'}
                  icon={'phoneOff'}
                  iconProps={{ color: 'var(--content-destructive-primary)' }}
                  onClick={() => {
                    handleBlock()
                    closeCallType()
                  }}
                />
              )}
              <Button
                typeBtn={'menuItem'}
                text={'Skip'}
                icon={'close'}
                onClick={() => {
                  handleSkipCallType()
                  defaultStore.setIncomingBlockInsightCardDismissed(true)
                }}
              />
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className={classNames(styles.section)}>
        <div className={classNames(styles.wrap)}>
          <div className={styles.header}>
            <TextField
              className={styles.textInput}
              size='small'
              variant='integrated'
              ariaLabel='CallTypeSearch'
              InputProps={{
                placeholder: 'Search',
              }}
              value={search}
              onChange={setSearch}
              mainActions={[makeTextFieldAutoFocus({ withFocus: !loading })]}
              rightActions={[makeTextFieldClear()]}
            />
          </div>
          <div className={styles.divider} />
          <div className={styles.content}>{content}</div>
          <div className={styles.divider} />
          <div className={styles.action}>
            {defaultStore.shouldShowIncomingBlockInsightCard && contact && (
              <Button
                textClassname={styles.blockButtonText}
                typeBtn={'menuItem'}
                text={'Block and report spam'}
                icon={'phoneOff'}
                iconProps={{ color: 'var(--content-destructive-primary)' }}
                onClick={() => {
                  handleBlock()
                  closeCallType()
                }}
              />
            )}
            <Button
              typeBtn={'menuItem'}
              text={'Skip call type'}
              icon={'close'}
              disabled={loading}
              onClick={handleSkipCallType}
            />
          </div>
        </div>
      </div>
    )
  }
)
